import { useState, useCallback, useEffect, useRef } from "react";
import { AgGrid, AgGridReact, toast } from "@jsq/ds/core";
import { useCreateSavedViewMutation, useGetSavedViewQuery } from "#graphql/supergraphHooks";
import type { GetSavedViewQuery } from "#graphql/supergraphTypes";

type ColumnDefs = AgGridReact.AgGridReactProps["columnDefs"];

/**
 * Subset of AgGrid.GridState that we persist per saved view.
 * Mirrors the shape used by TicketsTable's useGridViewManager.
 */
type SavableGridConfiguration = Pick<
  AgGrid.GridState,
  "columnVisibility" | "columnSizing" | "columnOrder" | "filter" | "sort"
>;

export interface UseWorkflowViewManagerProps {
  getColumnDefs: () => ColumnDefs;
  gridApi?: AgGrid.GridApi | null;
  skip?: boolean;
  hasInitialUrlFilters?: boolean;
}

export interface UseWorkflowViewManagerReturn {
  columnDefs: ColumnDefs | null;
  shouldShowGrid: boolean;
  saveViewLoading: boolean;
  handleSavedViewClicked: (api: AgGrid.GridApi) => Promise<void>;
  savedConfiguration: SavableGridConfiguration | null;
}

const isSavableGridConfiguration = (
  config: NonNullable<GetSavedViewQuery["getSavedView"]>["configuration"] | undefined,
): config is SavableGridConfiguration => {
  if (!config || typeof config !== "object") return false;
  return true;
};

const applyVisibilityAndSizing = (
  colDef: AgGrid.ColDef | AgGrid.ColGroupDef,
  columnVisibility?: SavableGridConfiguration["columnVisibility"],
  columnSizing?: SavableGridConfiguration["columnSizing"],
): AgGrid.ColDef | AgGrid.ColGroupDef => {
  if (!("field" in colDef) || !colDef.field) return colDef;
  const colId = colDef.field;
  const updatedColDef = { ...colDef } as AgGrid.ColDef;

  if (columnVisibility?.hiddenColIds) {
    updatedColDef.hide = columnVisibility.hiddenColIds.includes(colId);
  }

  const savedWidth = columnSizing?.columnSizingModel?.find(
    (c: { colId: string; width?: number }) => c.colId === colId,
  )?.width;
  if (savedWidth) {
    updatedColDef.width = savedWidth;
  }

  if ("sort" in updatedColDef) {
    delete updatedColDef.sort;
  }

  return updatedColDef;
};

const sortColumnsByOrder = (
  defs: NonNullable<ColumnDefs>,
  columnOrder?: SavableGridConfiguration["columnOrder"],
): NonNullable<ColumnDefs> => {
  if (!columnOrder?.orderedColIds?.length) return defs;
  return [...defs].sort((a, b) => {
    const aField = "field" in a ? a.field : undefined;
    const bField = "field" in b ? b.field : undefined;
    const aIndex = aField ? columnOrder.orderedColIds.indexOf(aField) : -1;
    const bIndex = bField ? columnOrder.orderedColIds.indexOf(bField) : -1;
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

const applyConfigToColumnDefs = (
  config: SavableGridConfiguration,
  baseDefs: NonNullable<ColumnDefs>,
): NonNullable<ColumnDefs> => {
  const { columnVisibility, columnOrder, columnSizing } = config;
  const withVisibilityAndSizing = baseDefs.map((def: AgGrid.ColDef | AgGrid.ColGroupDef) =>
    applyVisibilityAndSizing(def, columnVisibility, columnSizing),
  );
  return sortColumnsByOrder(withVisibilityAndSizing, columnOrder);
};

export const useWorkflowViewManager = ({
  getColumnDefs,
  gridApi,
  skip = false,
  hasInitialUrlFilters = false,
}: UseWorkflowViewManagerProps): UseWorkflowViewManagerReturn => {
  const [columnDefs, setColumnDefs] = useState<ColumnDefs | null>(null);
  const [shouldShowGrid, setShouldShowGrid] = useState(false);
  const [savedConfiguration, setSavedConfiguration] = useState<SavableGridConfiguration | null>(
    null,
  );
  const [savedFilterModel, setSavedFilterModel] = useState<AgGrid.FilterModel | null>(null);
  const [savedSortModel, setSavedSortModel] = useState<AgGrid.SortModelItem[] | null>(null);

  const appliedFilterRef = useRef(false);
  const appliedSortRef = useRef(false);
  const hadInitialUrlFilters = useRef(hasInitialUrlFilters);
  const initializedRef = useRef(false);
  const savedConfigurationRef = useRef<SavableGridConfiguration | null>(null);

  const handleGetSavedViewCompleted = useCallback(
    (data: GetSavedViewQuery) => {
      const rawConfig = data.getSavedView?.configuration;
      const defaultDefs = getColumnDefs() ?? [];

      if (!isSavableGridConfiguration(rawConfig)) {
        setColumnDefs(defaultDefs);
        setShouldShowGrid(true);
        initializedRef.current = true;
        return;
      }

      const appliedDefs = applyConfigToColumnDefs(rawConfig, defaultDefs);

      savedConfigurationRef.current = rawConfig;
      setSavedConfiguration(rawConfig);
      setSavedFilterModel(rawConfig.filter?.filterModel ?? null);
      setSavedSortModel(rawConfig.sort?.sortModel ?? null);
      setColumnDefs(appliedDefs);
      setShouldShowGrid(true);
      initializedRef.current = true;
    },
    [getColumnDefs],
  );

  const handleGetSavedViewError = useCallback(() => {
    toast.error("Failed to load saved view");
    setColumnDefs(getColumnDefs() ?? []);
    setShouldShowGrid(true);
    initializedRef.current = true;
  }, [getColumnDefs]);

  useEffect(() => {
    if (!initializedRef.current) return;
    const defaultDefs = getColumnDefs() ?? [];
    setColumnDefs(
      savedConfigurationRef.current
        ? applyConfigToColumnDefs(savedConfigurationRef.current, defaultDefs)
        : defaultDefs,
    );
  }, [getColumnDefs]);

  useGetSavedViewQuery({
    onCompleted: handleGetSavedViewCompleted,
    onError: handleGetSavedViewError,
    fetchPolicy: "no-cache",
    skip,
  });

  useEffect(() => {
    if (!skip || initializedRef.current) return;
    const defs = getColumnDefs();
    if (!defs) return;
    setColumnDefs(defs);
    setShouldShowGrid(true);
    initializedRef.current = true;
  }, [skip, getColumnDefs]);

  useEffect(() => {
    if (!gridApi || !savedFilterModel || appliedFilterRef.current) return;
    if (hadInitialUrlFilters.current) return;
    if (Object.keys(savedFilterModel).length === 0) return;
    appliedFilterRef.current = true;
    gridApi.setFilterModel(savedFilterModel);
    setSavedFilterModel(null);
  }, [gridApi, savedFilterModel]);

  useEffect(() => {
    if (!gridApi || !savedSortModel || appliedSortRef.current) return;
    if (savedSortModel.length === 0) return;
    appliedSortRef.current = true;
    gridApi.applyColumnState({
      state: savedSortModel.map(({ colId, sort }) => ({ colId, sort })),
    });
    setSavedSortModel(null);
  }, [gridApi, savedSortModel]);

  const [createSavedView, { loading: saveViewLoading }] = useCreateSavedViewMutation({
    onCompleted: () => {
      toast.success("View saved successfully");
    },
    onError: () => {
      toast.error("Failed to save view");
    },
  });

  const buildSavedViewConfiguration = useCallback(
    (api: AgGrid.GridApi): SavableGridConfiguration => {
      const columnState = api.getColumnState();
      const filterModel = api.getFilterModel();

      const hiddenColIds = columnState.filter((c) => c.hide).map((c) => c.colId);
      const columnSizingModel = columnState
        .filter((c) => c.width != null)
        .map((c) => ({ colId: c.colId, width: c.width as number }));
      const orderedColIds = columnState.map((c) => c.colId);
      const sortModel = columnState
        .filter((c) => c.sort != null)
        .map((c) => ({ colId: c.colId, sort: c.sort as "asc" | "desc" }));

      return {
        columnVisibility: { hiddenColIds },
        columnSizing: { columnSizingModel },
        columnOrder: { orderedColIds },
        filter: { filterModel },
        ...(sortModel.length > 0 && { sort: { sortModel } }),
      };
    },
    [],
  );

  const handleSavedViewClicked = useCallback(
    async (api: AgGrid.GridApi) => {
      const configuration = buildSavedViewConfiguration(api);
      await createSavedView({ variables: { input: { configuration } } });
    },
    [createSavedView, buildSavedViewConfiguration],
  );

  return {
    columnDefs,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    savedConfiguration,
  };
};

export type { SavableGridConfiguration };
